import { useEffect, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Bell, Check, Dot } from "lucide-react";
import { toast } from "sonner";
import { api, API } from "../lib/api";

function timeAgo(iso) {
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

export function NotificationBell() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);
  const unread = items.filter((n) => !n.read).length;

  const load = useCallback(() => {
    api.get("/notifications?limit=30").then(({ data }) => setItems(data.notifications || [])).catch(() => {});
  }, []);

  useEffect(() => {
    load();
    const token = localStorage.getItem("wsbf_token");
    if (!token) return;
    let es;
    try {
      es = new EventSource(`${API}/notifications/stream?token=${encodeURIComponent(token)}`);
      es.onmessage = (e) => {
        try {
          const ev = JSON.parse(e.data);
          if (ev.type === "notification") {
            setItems((prev) => [ev.payload, ...prev].slice(0, 40));
            toast(ev.payload.title, { description: ev.payload.body });
          }
        } catch { /* keepalive */ }
      };
    } catch { /* ignore */ }
    return () => es && es.close();
  }, [load]);

  useEffect(() => {
    const onClick = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const markAll = async () => {
    setItems((prev) => prev.map((n) => ({ ...n, read: true })));
    try { await api.post("/notifications/read-all"); } catch { /* ignore */ }
  };

  return (
    <div className="relative" ref={boxRef}>
      <button
        data-testid="notif-bell"
        onClick={() => setOpen((o) => !o)}
        className="relative p-2 rounded-lg text-slate-300 hover:text-[#E5B842]"
        style={{ transition: "color 0.15s ease" }}
        title="Notifications"
      >
        <Bell className="w-5 h-5" />
        {unread > 0 && (
          <span
            data-testid="notif-badge"
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full bg-[#E5B842] text-[#000000] text-[10px] font-black tabular-nums"
          >
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          data-testid="notif-dropdown"
          className="absolute right-0 mt-2 w-80 max-h-[420px] overflow-hidden rounded-xl bg-[#0f0f0f] border border-[#2b2512] shadow-2xl z-50 flex flex-col"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-[#2b2512]">
            <span className="font-heading font-bold uppercase tracking-wide text-slate-50 text-sm">Notifications</span>
            {unread > 0 && (
              <button data-testid="notif-mark-all" onClick={markAll} className="text-[11px] font-mono uppercase text-[#E5B842] hover:underline flex items-center gap-1">
                <Check className="w-3 h-3" /> Mark all
              </button>
            )}
          </div>
          <div className="overflow-y-auto">
            {items.length === 0 ? (
              <div className="py-10 text-center text-slate-500 font-mono text-xs uppercase tracking-widest">No notifications</div>
            ) : (
              items.map((n) => {
                const inner = (
                  <div className="flex gap-2.5 px-4 py-3 border-b border-[#161616] hover:bg-[#0d0d0d]" style={{ transition: "background-color 0.15s ease" }}>
                    <div className="pt-0.5">
                      {!n.read ? <Dot className="w-4 h-4 text-[#E5B842]" /> : <Dot className="w-4 h-4 text-transparent" />}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold text-slate-100 truncate">{n.title}</div>
                      <div className="text-xs text-slate-400 leading-snug">{n.body}</div>
                      <div className="text-[10px] font-mono text-slate-600 mt-1">{n.public_match_id ? `${n.public_match_id} · ` : ""}{timeAgo(n.created_at)}</div>
                    </div>
                  </div>
                );
                const to = n.match_id ? `/match/${n.match_id}` : n.meta?.link;
                return to ? (
                  <Link key={n.id} to={to} data-testid={`notif-item-${n.id}`} onClick={() => setOpen(false)}>{inner}</Link>
                ) : (
                  <div key={n.id} data-testid={`notif-item-${n.id}`}>{inner}</div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
